import * as THREE from 'three'
import { ARENA_RADIUS } from '../config'
import {
  advanceStagger,
  applyStagger,
  clamp,
  commandPhaseFor,
  directHitMultiplier,
  type StaggerState,
} from '../mechanics'
import { animateMech, createMech, type MechRig } from '../render/MechFactory'
import type { Arena } from '../world/Arena'

export type EnemyClass = 'skirmisher' | 'bulwark'

export interface EnemyShot {
  origin: THREE.Vector3
  direction: THREE.Vector3
  damageScale: number
}

export interface HitOutcome {
  destroyed: boolean
  overloaded: boolean
  damageApplied: number
  /** True on the single frame a command unit crosses into its second phase. */
  phaseShifted: boolean
}

export class EnemyUnit {
  readonly rig: MechRig
  readonly velocity = new THREE.Vector3()
  readonly maximumArmor: number
  readonly maximumStagger: number
  armor: number
  stagger: StaggerState = { value: 0, overloadedFor: 0 }
  private shotTimer: number
  private orbitDirection: number
  private flinch = 0
  private flinchYaw = 0
  /** Command units escalate once, below half armour. Skirmishers stay at 1. */
  private phase: 1 | 2 = 1

  constructor(
    scene: THREE.Scene,
    readonly id: string,
    readonly enemyClass: EnemyClass,
    position: THREE.Vector3,
    phaseOffset: number,
    private readonly arena: Arena,
  ) {
    this.maximumArmor = enemyClass === 'bulwark' ? 6_600 : 3_700
    this.maximumStagger = enemyClass === 'bulwark' ? 135 : 100
    this.armor = this.maximumArmor
    this.shotTimer = 0.65 + phaseOffset * 0.43
    this.orbitDirection = phaseOffset % 2 > 1 ? -1 : 1
    this.rig = createMech({ faction: 'enemy', heavy: enemyClass === 'bulwark' })
    this.rig.root.position.copy(position)
    scene.add(this.rig.root)
  }

  get alive(): boolean {
    return this.armor > 0
  }

  /** 1 for every skirmisher; 2 once a command unit has been pushed past half. */
  get combatPhase(): 1 | 2 {
    return this.phase
  }

  private get isCommandUnit(): boolean {
    return this.enemyClass === 'bulwark'
  }

  get position(): THREE.Vector3 {
    return this.rig.root.position
  }

  get aimPoint(): THREE.Vector3 {
    return this.position.clone().add(new THREE.Vector3(0, this.enemyClass === 'bulwark' ? 5.7 : 4.8, 0))
  }

  update(deltaSeconds: number, time: number, playerPosition: THREE.Vector3): EnemyShot | null {
    if (!this.alive) return null
    this.stagger = advanceStagger(this.stagger, deltaSeconds, this.enemyClass === 'bulwark' ? 11 : 17)
    this.shotTimer -= deltaSeconds
    this.flinch *= Math.exp(-8 * deltaSeconds)

    const toPlayer = playerPosition.clone().sub(this.position)
    const distance = Math.max(0.001, toPlayer.length())
    const horizontal = toPlayer.clone().setY(0).normalize()
    const tangent = new THREE.Vector3(-horizontal.z, 0, horizontal.x).multiplyScalar(this.orbitDirection)
    const escalated = this.phase === 2
    const preferred = this.isCommandUnit ? (escalated ? 21 : 33) : 25
    const radialCorrection = clamp((distance - preferred) / 14, -1, 1)
    const desired = tangent.multiplyScalar(0.76).add(horizontal.multiplyScalar(radialCorrection)).normalize()
    const moveSpeed = this.isCommandUnit ? (escalated ? 8.6 : 5.2) : 8.3

    if (this.stagger.overloadedFor > 0) {
      this.velocity.multiplyScalar(Math.exp(-8 * deltaSeconds))
    } else {
      this.velocity.lerp(desired.multiplyScalar(moveSpeed), 1 - Math.exp(-2.7 * deltaSeconds))
      const previousPosition = this.position.clone()
      this.position.addScaledVector(this.velocity, deltaSeconds)
      const radius = Math.hypot(this.position.x, this.position.z)
      if (radius > ARENA_RADIUS - 5) {
        this.position.x *= (ARENA_RADIUS - 5) / radius
        this.position.z *= (ARENA_RADIUS - 5) / radius
        this.orbitDirection *= -1
      }
      if (
        this.arena.resolveBody(
          this.position,
          this.enemyClass === 'bulwark' ? 2.8 : 2.15,
          6.3,
          previousPosition,
        )
      ) {
        this.velocity.multiplyScalar(0.2)
        this.orbitDirection *= -1
      }
    }

    const desiredYaw = Math.atan2(toPlayer.x, -toPlayer.z)
    this.rig.root.rotation.y = dampAngle(this.rig.root.rotation.y, desiredYaw, 6.4, deltaSeconds)
    animateMech(this.rig, {
      time: time + this.id.length * 0.2,
      planarSpeed: this.velocity.length(),
      boosting: this.velocity.length() > 6.5,
      overloaded: this.stagger.overloadedFor > 0,
      flinch: this.flinch,
      flinchYaw: this.flinchYaw,
    })

    const interval = this.isCommandUnit ? (escalated ? 0.72 : 1.65) : 1.02
    const playerAim = playerPosition.clone().add(new THREE.Vector3(0, 4.2, 0))
    if (
      this.shotTimer <= 0 &&
      distance < 90 &&
      this.stagger.overloadedFor === 0 &&
      this.arena.hasLineOfSight(this.aimPoint, playerAim, 0.12)
    ) {
      this.shotTimer = interval + Math.sin(time * 1.7 + this.id.length) * 0.16
      const origin = this.aimPoint
      const lead = playerAim.sub(origin).normalize()
      const damageScale = this.isCommandUnit ? (escalated ? 1.55 : 1.3) : 0.85
      return { origin, direction: lead, damageScale }
    }
    return null
  }

  applyHit(damage: number, impact: number, source?: THREE.Vector3): HitOutcome {
    const wasOverloaded = this.stagger.overloadedFor > 0
    const damageApplied = damage * directHitMultiplier(this.stagger)
    this.armor = clamp(this.armor - damageApplied, 0, this.maximumArmor)
    this.stagger = applyStagger(this.stagger, impact, this.maximumStagger, this.enemyClass === 'bulwark' ? 2.7 : 2.2)
    // Heavier frames absorb more of the blow than they show.
    const give = this.enemyClass === 'bulwark' ? 44 : 26
    this.flinch = Math.min(1, this.flinch + clamp(impact / give, 0.1, 0.65))
    if (source) {
      const incoming = source.clone().sub(this.position).setY(0)
      if (incoming.lengthSq() > 0.0001) {
        this.flinchYaw = Math.atan2(incoming.x, -incoming.z) - this.rig.root.rotation.y
      }
    }
    const previousPhase = this.phase
    if (this.isCommandUnit) this.phase = commandPhaseFor(this.armor, this.maximumArmor)

    return {
      destroyed: this.armor === 0,
      overloaded: !wasOverloaded && this.stagger.overloadedFor > 0,
      damageApplied,
      phaseShifted: this.phase !== previousPhase,
    }
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.rig.root)
    this.rig.root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return
      object.geometry.dispose()
      if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose())
      else object.material.dispose()
    })
  }
}

function dampAngle(current: number, target: number, speed: number, deltaSeconds: number): number {
  const difference = Math.atan2(Math.sin(target - current), Math.cos(target - current))
  return current + difference * (1 - Math.exp(-speed * deltaSeconds))
}
